import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, LocationMarkerIcon } from '@heroicons/react/outline';
import { feature } from '../utils/features';
import { LoadingBlock } from './Spinner';

import { cityListQuery } from '../utils/Data';
import { client } from '../client';

const City = ({ city, numRoles }) => {
  return (
    <div className="xl:w-1/3 md:w-1/2 p-2 cursor-pointer">
      <Link to={`/jobs/city/${encodeURIComponent(city)}`}>
        <div className="border border-gray-200 p-4 rounded-lg">
          <div className="w-8 h-8 inline-flex items-center justify-center rounded-full p-1 bg-primary-light text-primary-dark mb-4">
            <LocationMarkerIcon />
          </div>
          <h2 className="text-lg text-gray-900 font-medium title-font mb-2">
            {city}
          </h2>
          <p className="leading-relaxed text-base">
            Number of roles available:{' '}
            <span className="font-bold">{numRoles}</span>
          </p>
        </div>
      </Link>
    </div>
  );
};

// Refreshed city tile (feature "modernCards").
const ModernCity = ({ city, numRoles }) => (
  <Link
    to={`/jobs/city/${encodeURIComponent(city)}`}
    className="group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200 transition hover:-translate-y-0.5 hover:shadow-lg hover:ring-primary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
  >
    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-light text-primary-dark">
      <LocationMarkerIcon className="h-6 w-6" aria-hidden="true" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate font-display text-lg font-bold text-gray-900 group-hover:text-primary-dark">{city.trim()}</span>
      <span className="text-sm text-gray-500">
        {numRoles} {numRoles === 1 ? 'job' : 'jobs'}
      </span>
    </span>
    <ArrowRightIcon className="h-5 w-5 text-gray-300 transition group-hover:translate-x-1 group-hover:text-primary-dark" aria-hidden="true" />
  </Link>
);

const MAX_TILES = 6;

function Cities() {
  const [topCities, setTopCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .fetch(cityListQuery)
      .then((cities) => {
        // Count exact spellings, then keep only the most common spelling per city
        // (the data has variants like "Surrey " and "surrey"). The tile links to
        // and counts that exact spelling, so it matches the city page's results.
        const counts = {};
        (cities || []).forEach((city) => {
          counts[city] = (counts[city] || 0) + 1;
        });
        const best = {};
        Object.entries(counts).forEach(([city, count]) => {
          const key = city.trim().toLowerCase();
          if (!best[key] || count > best[key].count) best[key] = { city, count };
        });
        const ranked = Object.values(best)
          .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city))
          .slice(0, MAX_TILES);
        setTopCities(ranked);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, []);

  if (feature('modernCards')) {
    return (
      <section aria-labelledby="cities" className="bg-gray-50 py-20">
        <div className="mx-auto max-w-6xl px-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 id="cities" className="font-display text-3xl font-bold text-gray-900">
                Jobs by city
              </h2>
              <p className="mt-2 text-gray-600">Where employers are hiring right now.</p>
            </div>
            <Link to="/jobs" className="inline-flex items-center gap-1 font-semibold text-primary-dark hover:underline">
              All jobs <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          {loading ? (
            <LoadingBlock label="Loading cities…" minHeight="min-h-[12rem]" />
          ) : (
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {topCities.map(({ city, count }) => (
                <ModernCity key={city} city={city} numRoles={count} />
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="text-gray-600 body-font">
      <div className="container px-5 py-24 mx-auto">
        <div className="flex flex-wrap w-full mb-20 flex-col items-center text-center">
          <h1 className="sm:text-3xl text-2xl font-medium title-font mb-2 text-gray-900">
            Search By Major Cities
          </h1>
          <p className="lg:w-1/2 w-full leading-relaxed text-gray-500">
            Your next job is waiting for you!
          </p>
        </div>
        {loading && <LoadingBlock label="Loading cities…" minHeight="min-h-[12rem]" />}
        <div className="flex flex-wrap justify-center  md:-m-4">
          {topCities.map(({ city, count }) => (
            <City key={city} city={city} numRoles={count} />
          ))}
        </div>

        <Link to="/jobs">
          <button className="flex mx-auto mt-16 text-white font-[Poppins] bg-primary px-6 py-1 rounded-lg border-2 border-primary hover:text-primary-dark hover:bg-white ease-in duration-150 text-lg">
            See All
          </button>
        </Link>
      </div>
    </section>
  );
}

export default Cities;
